import { v4 as uuid } from "uuid";
import { WidgetTypes } from "../components/widgetDashboard/widgetDashboard.enums";
import { getDefaultLayout, getDefaultWidget } from "../components/widgetDashboard/widgetDashboard.helper";
import { DashboardSyncSettings, IWidgetDashboard, WidgetConfig } from "../components/widgetDashboard/widgetDashboard.interface";
import { Security } from "../proto/generated/candle-data-depot-common";
import { selectActiveLayout, selectActiveLayoutId, selectPrimarySecurity } from "../selectors/home";
import { selectActiveWidget, selectAllLayouts, selectSyncSettings } from "../selectors/widgetDashboard";
import store from "../store";
import { selectActiveChartId, selectChartConfigs, selectDrawings, selectTimeBuckets } from "../selectors/chart";
import { getSecurityKey } from "./security";
import { getDefaultChartConfig, getDefaultChartPanesConfig } from "./chart";
import { getSavedLayout } from "./widgetDashboard";
import { updateSyncSettings } from "../reducer/widgetDashboard.reducer";

class LayoutManager {
    private dashboard?: IWidgetDashboard

    setDashboard(dashboard: IWidgetDashboard) {
        this.dashboard = dashboard;
    }
    getActiveLayoutId(): string {
        return selectActiveLayoutId(store.getState())
    }
    getActiveWidget() {
        return selectActiveWidget(this.getActiveLayoutId())(store.getState())
    }

    getActiveLayout() {
        return selectActiveLayout(store.getState())
    }

    splitLayout(numberOfDivision = 2) {
        const landscape = window.matchMedia("(orientation: landscape)");
        const layout = this.getActiveLayout();
        const root = layout.panes;
        if (!root || root.panes.length > 1) return;
        this.dashboard?.splitPane(root.id, numberOfDivision, landscape.matches);
    }

    unsplitLayout() {
        const layout = this.getActiveLayout();
        const root = layout.panes;
        if (!root || root.panes.length == 1) return;
        this.deletePane(root.panes[1].id);
    }

    deletePane(paneId: number) {
        this.dashboard?.deletePane(paneId);
    }
    updateWidgetConfig(slotId: number, settings: WidgetConfig<any>['settings']) {
        this.dashboard?.updateWidgetConfig(slotId, settings)
    }
    updateSecurity(key: string, expressionMode: boolean, securities: Security[], displayName?: string) {
        this.dashboard?.updateSecurity(key, expressionMode, securities, displayName);
    }
    getLayoutState(): Record<string, string> {
        const state: Record<string, string> = {};

        if (!window.chartManager) return {};
        const layout = this.getActiveLayout();
        Object.keys(layout.widgets).forEach((wId) => {
            const widget = layout.widgets[Number(wId)];
            if (widget.widgetType === WidgetTypes.CHART) {
                const chartId = widget.settings.chartId;
                if (!chartId) return;
                const chart = window.chartManager.getChart(chartId);
                state[chartId] = JSON.stringify(chart.getState());
            }
        })
        return state;
    }

    async getNewLayoutString(): Promise<string> {
        const state = store.getState();
        const {expressionMode, key, expressionSecurities, displayName} = selectPrimarySecurity(state);
        const newLayoutId = uuid().replace(/[-]*/g, '').split('').slice(0, 10).join('')
        const dashboard = getDefaultLayout()
        const chartConfigs = selectChartConfigs(state);
        const timeBuckets = selectTimeBuckets(state);

        const activeChartId = selectActiveChartId(state);
        const timeBucket = chartConfigs[activeChartId].timeBucket ?? timeBuckets.find(t => t.code === "ONE_MINUTE")
        const securityList = expressionMode ? expressionSecurities.map(v => getSecurityKey(v.exchange, v.code)) : [key]
        const widget = getDefaultWidget(newLayoutId, 1, WidgetTypes.CHART, key, expressionMode, securityList, timeBucket)
        dashboard.widgets[widget.slotId] = widget;
        const chartViewKey = widget.settings.chartId
        const chartConfig = getDefaultChartConfig({
            timeBucket
        })
        const {
            primaryPane,
            seriesGroups,
            seriesObject
        } = await getDefaultChartPanesConfig(chartConfig.chartType, expressionMode, expressionMode ? key : '', securityList, [], undefined, displayName)

        chartConfig.panes = [primaryPane.id]
        chartConfig.panesConfig = {
            [primaryPane.id]: primaryPane
        }
        chartConfig.seriesGroups = seriesGroups;
        chartConfig.series = seriesObject;

    
        const layoutToBeSaved = {
            panes: dashboard.panes,
            widgets: {
                [widget.slotId]: widget
            },
            syncSettings: dashboard.syncSettings,
            charts: {
                [chartViewKey]: chartConfig
            },
            drawings: {
                [chartViewKey]: []
            }
        }
        return JSON.stringify(layoutToBeSaved);
    }

    updateSyncSettings(layoutId: string, key: keyof DashboardSyncSettings, value: boolean) {
        const syncSettings = selectSyncSettings(layoutId)(store.getState());
        store.dispatch(
            updateSyncSettings({
                    id: layoutId,
                    syncSettings: {
                        ...syncSettings,
                        [key]: value
                    }
                })
            )
    }

    getSaveLayoutString(): string {
        const state = store.getState();
        const activeLayoutId = selectActiveLayoutId(state);
        const layouts = selectAllLayouts(state);
        const chartConfigs = selectChartConfigs(state);
        const drawings = selectDrawings(state)
        const layoutToBeSaved = getSavedLayout(activeLayoutId, layouts[activeLayoutId], chartConfigs, drawings);
        return JSON.stringify(layoutToBeSaved);
    }

}

export default LayoutManager;